<template>
  <div
    v-if="hasCurrentUsername"
    class="sidebar-profile"
  >
    <div
      ref="trigger"
      class="sidebar-footer-card sidebar-footer-card--profile"
      :class="{ 'sidebar-footer-card--unlimited': isUnlimitedPlan }"
      role="button"
      tabindex="0"
      :aria-expanded="profileDropdownOpen ? 'true' : 'false'"
      aria-haspopup="true"
      aria-controls="sidebar-profile-menu"
      @click="onTriggerClick"
      @keydown="onTriggerKeydown"
    >
      <div class="sidebar-footer-card__avatar">
        <img
          v-if="displayAvatarUrl"
          class="sidebar-footer-card__avatar-image"
          :src="displayAvatarUrl"
          :alt="currentUsername || 'User avatar'"
          @error="onAvatarImageError"
        />
        <i v-else class="ti-user sidebar-footer-card__avatar-icon"></i>
      </div>
      <div
        v-if="hasCurrentUsername"
        class="sidebar-footer-card__title"
        :title="currentUsername"
      >
        {{ displayUsername }}
      </div>
      <div
        class="sidebar-footer-card__subtitle"
        :class="isUnlimitedPlan ? 'sidebar-footer-card__subtitle--unlimited' : 'sidebar-footer-card__subtitle--free'"
      >
        <i
          v-if="isUnlimitedPlan"
          class="fa-solid fa-crown sidebar-footer-card__subtitle-icon"
          aria-hidden="true"
        ></i>
        <span>{{ planLabel }}</span>
      </div>
      <div
        class="sidebar-profile-pill__chevron"
        :class="{ 'sidebar-profile-pill__chevron--open': profileDropdownOpen }"
        aria-hidden="true"
      >
        <i class="ti-angle-right"></i>
      </div>
    </div>
    <body-portal v-if="profileDropdownOpen">
      <div
        v-if="presentationMode === 'modal'"
        class="sidebar-profile-pill__backdrop"
        @click="closeProfileDropdown"
      />
      <ul
        id="sidebar-profile-menu"
        class="sidebar-profile-pill__menu"
        :class="{ 'sidebar-profile-pill__menu--modal': presentationMode === 'modal' }"
        :style="menuStyle"
        role="dialog"
        :aria-modal="presentationMode === 'modal' ? 'true' : undefined"
        :aria-label="menuAriaLabel"
      >
        <template v-if="menuView === 'main'">
          <li role="none" class="sidebar-profile-pill__tokens-panel">
            <div class="sidebar-profile-pill__tokens-label">
              {{ tt("navbar.tokensAvailable", "Available tokens") }}
            </div>
            <div
              class="sidebar-profile-pill__tokens-value"
              :class="{ 'sidebar-profile-pill__tokens-value--low': creditsAreLow }"
            >
              <i class="fa-solid fa-coins sidebar-profile-pill__tokens-icon" aria-hidden="true"></i>
              <span class="sidebar-profile-pill__tokens-amount">{{ formattedCreditsRemaining }}</span>
            </div>
            <div class="sidebar-profile-pill__tokens-hint">
              {{ tokenGrantDescription }}
            </div>
            <div v-if="tokenRenewalDescription" class="sidebar-profile-pill__tokens-renewal">
              {{ tokenRenewalDescription }}
            </div>
          </li>
          <li role="none" class="sidebar-profile-pill__menu-profile">
            <button
              type="button"
              class="sidebar-profile-pill__profile-btn"
              @click.stop="openProfile"
            >
              <i class="ti-user" aria-hidden="true"></i>
              <span>{{ tt("navbar.menuProfile", "Perfil") }}</span>
            </button>
          </li>
          <li role="none" class="sidebar-profile-pill__menu-language">
            <div class="sidebar-profile-pill__language-label">
              {{ tt("navbar.menuLanguage", "Language") }}
            </div>
            <div class="sidebar-profile-pill__language-row">
              <button
                type="button"
                class="sidebar-profile-pill__language-btn"
                :class="{ 'sidebar-profile-pill__language-btn--active': isLocaleActive('es') }"
                :disabled="isSavingLanguage"
                :aria-pressed="isLocaleActive('es') ? 'true' : 'false'"
                @click.stop="onSelectLanguage('es')"
              >
                {{ tt("common.spanish", "Spanish") }}
              </button>
              <button
                type="button"
                class="sidebar-profile-pill__language-btn"
                :class="{ 'sidebar-profile-pill__language-btn--active': isLocaleActive('en') }"
                :disabled="isSavingLanguage"
                :aria-pressed="isLocaleActive('en') ? 'true' : 'false'"
                @click.stop="onSelectLanguage('en')"
              >
                {{ tt("common.english", "English") }}
              </button>
            </div>
          </li>
          <li role="none" class="sidebar-profile-pill__menu-logout">
            <button
              type="button"
              class="sidebar-profile-pill__logout-btn"
              @click.stop="handleLogout"
            >
              <i class="fa-solid fa-arrow-right-from-bracket" aria-hidden="true"></i>
              <span>{{ tt("nav.logout", "Cerrar sesión") }}</span>
            </button>
          </li>
          <li role="none" class="sidebar-profile-pill__menu-help">
            <button
              type="button"
              class="sidebar-profile-pill__help-btn"
              @click.stop="openHelpView"
            >
              <i class="fa-regular fa-circle-question" aria-hidden="true"></i>
              <span>{{ tt("nav.help", "Ayuda") }}</span>
            </button>
          </li>
        </template>
        <template v-else>
          <li role="none" class="sidebar-profile-pill__menu-back">
            <button
              type="button"
              class="sidebar-profile-pill__back-btn"
              @click.stop="menuView = 'main'"
            >
              <i class="ti-angle-left" aria-hidden="true"></i>
              <span>{{ tt("nav.helpBack", "Volver") }}</span>
            </button>
          </li>
          <li role="none" class="sidebar-profile-pill__menu-action">
            <button
              type="button"
              class="sidebar-profile-pill__action-btn"
              @click.stop="openSuggestion"
            >
              <i class="fa-regular fa-lightbulb" aria-hidden="true"></i>
              <span>{{ tt("nav.suggestions", "Sugerencias") }}</span>
            </button>
          </li>
          <li role="none" class="sidebar-profile-pill__menu-action">
            <a
              class="sidebar-profile-pill__action-btn sidebar-profile-pill__action-btn--whatsapp"
              :href="whatsappHref"
              target="_blank"
              rel="noopener noreferrer"
              @click="closeProfileDropdown"
            >
              <i class="fa-brands fa-whatsapp" aria-hidden="true"></i>
              <span>{{ tt("app.whatsapp.title", "WhatsApp") }}</span>
            </a>
          </li>
          <li role="none" class="sidebar-profile-pill__menu-action sidebar-profile-pill__menu-action--soon">
            <button
              type="button"
              class="sidebar-profile-pill__action-btn sidebar-profile-pill__action-btn--disabled"
              disabled
              aria-disabled="true"
            >
              <span class="sidebar-profile-pill__coming-soon">
                {{ tt("nav.comingSoonTag", "Coming soon") }}
              </span>
              <i class="fa-solid fa-graduation-cap" aria-hidden="true"></i>
              <span>{{ tt("nav.tutorials", "Tutoriales") }}</span>
            </button>
          </li>
        </template>
      </ul>
    </body-portal>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import { initializeLocale } from "@/i18n";
import { buildAuthHeaders } from "@/utils/impersonation";
import { leaveAppForWebsite } from "@/utils/auth";
import { useCurrentUserStore } from "@/stores/currentUser";
import {
  MOBILE_PROFILE_MENU_OPEN_EVENT,
  OPEN_SUGGESTIONS_MODAL_EVENT,
  PROFILE_MENU_STATE_EVENT,
} from "@/config/navRoutes";

export default {
  data() {
    return {
      profileDropdownOpen: false,
      presentationMode: "flyout",
      menuView: "main",
      isSavingLanguage: false,
      avatarLoadFailed: false,
      menuStyle: {},
    };
  },
  computed: {
    currentUserStore() {
      return useCurrentUserStore();
    },
    currentUsername() {
      return this.currentUserStore.displayName;
    },
    currentUserAvatarUrl() {
      const value = this.currentUserStore.avatarUrl;
      return typeof value === "string" ? value.trim() : "";
    },
    displayAvatarUrl() {
      if (this.avatarLoadFailed) return "";
      return this.currentUserAvatarUrl;
    },
    hasCurrentUsername() {
      return typeof this.currentUsername === "string" && this.currentUsername.trim().length > 0;
    },
    isUnlimitedPlan() {
      const tier = String(this.currentUserStore.planTier || "").trim().toLowerCase();
      if (tier === "unlimited" || tier === "legacy") return true;
      return this.currentUserStore.hasActiveSubscription === true;
    },
    planLabel() {
      return this.isUnlimitedPlan
        ? this.tt("billing.unlimitedPlanName", "Unlimited")
        : this.tt("billing.freePlanName", "Free");
    },
    creditsRemaining() {
      return this.currentUserStore.creditsRemaining;
    },
    hasCreditsRemaining() {
      return Number.isFinite(this.creditsRemaining);
    },
    formattedCreditsRemaining() {
      if (!this.hasCreditsRemaining) return "—";
      return this.creditsRemaining.toLocaleString();
    },
    creditsAreLow() {
      if (!this.hasCreditsRemaining) return false;
      return this.creditsRemaining <= 10;
    },
    planTierNormalized() {
      return String(this.currentUserStore.planTier || "").trim().toLowerCase();
    },
    tokenGrantDescription() {
      const planPrefix = this.tt("navbar.planLabelPrefix", "Plan: ");
      if (this.planTierNormalized === "unlimited") {
        return `${planPrefix}${this.tt("billing.unlimitedFeatureTokens", "720 tokens al día")}`;
      }
      if (this.planTierNormalized === "legacy") {
        return this.tt("navbar.tokenPriceHint", "1 token = 1 minute");
      }
      return `${planPrefix}${this.tt("billing.freeFeatureTokens", "240 tokens al mes")}`;
    },
    tokenRenewalAt() {
      const billingExpires = this.currentUserStore.billingStatus?.subscription_credits_expires_at;
      if (billingExpires) return billingExpires;

      const tier = this.planTierNormalized;
      if (tier === "legacy") {
        return this.currentUserStore.subscriptionStatus?.next_payment_date || null;
      }

      const now = new Date();
      if (tier === "unlimited") {
        return new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
        ).toISOString();
      }

      const year = now.getUTCFullYear();
      const month = now.getUTCMonth();
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      return new Date(Date.UTC(nextYear, nextMonth, 1)).toISOString();
    },
    formattedTokenRenewalDate() {
      const raw = this.tokenRenewalAt;
      if (!raw) return "";
      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return "";
      const locale = String(this.$i18n?.locale || "").toLowerCase().startsWith("es") ? "es-MX" : "en-US";
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(parsed);
    },
    tokenRenewalDescription() {
      const date = this.formattedTokenRenewalDate;
      if (!date) return "";
      return this.tt("billing.subscriptionRenewsOn", "Renueva el {date}").replace("{date}", date);
    },
    displayUsername() {
      const username = typeof this.currentUsername === "string" ? this.currentUsername.trim() : "";
      const maxLength = 16;
      if (username.length <= maxLength) return username;
      return `${username.slice(0, maxLength).trimEnd()}...`;
    },
    whatsappHref() {
      const userLanguage =
        this.currentUserStore?.profile?.user_default_lenguage ||
        this.currentUserStore?.ensuredProfile?.user_default_lenguage ||
        this.currentUserStore?.locale ||
        "es";
      const number = userLanguage === "en" ? "528112557887" : "525577169082";
      return `https://wa.me/${number}?text=${encodeURIComponent(
        this.tt("app.whatsapp.prefilledMessage", "")
      )}`;
    },
    menuAriaLabel() {
      if (this.menuView === "help") {
        return this.tt("nav.help", "Ayuda");
      }
      return this.tt("navbar.tokensAvailable", "Available tokens");
    },
  },
  watch: {
    currentUserAvatarUrl() {
      this.avatarLoadFailed = false;
    },
  },
  mounted() {
    window.addEventListener(MOBILE_PROFILE_MENU_OPEN_EVENT, this.onMobileProfileMenuOpen);
  },
  beforeDestroy() {
    window.removeEventListener(MOBILE_PROFILE_MENU_OPEN_EVENT, this.onMobileProfileMenuOpen);
    this.closeProfileDropdown();
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    onAvatarImageError() {
      this.avatarLoadFailed = true;
    },
    emitProfileMenuState(open) {
      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent(PROFILE_MENU_STATE_EVENT, {
          detail: { open: Boolean(open) },
        })
      );
    },
    onMobileProfileMenuOpen() {
      if (!this.hasCurrentUsername) return;
      if (this.profileDropdownOpen && this.presentationMode === "modal") {
        this.closeProfileDropdown();
        return;
      }
      this.openProfileMenuModal();
    },
    openProfileMenuModal() {
      this.presentationMode = "modal";
      this.menuView = "main";
      this.profileDropdownOpen = true;
      this.menuStyle = {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1060,
      };
      this.emitProfileMenuState(true);
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.profileDropdownOpen) {
            this.attachListeners();
          }
        }, 0);
      });
    },
    openHelpView() {
      this.menuView = "help";
    },
    openSuggestion() {
      this.closeProfileDropdown();
      if (typeof window === "undefined") return;
      window.dispatchEvent(new CustomEvent(OPEN_SUGGESTIONS_MODAL_EVENT));
    },
    openProfile() {
      const profilePath = "/user-profile";
      this.closeProfileDropdown();
      if (this.$route.path !== profilePath) {
        this.$router.push(profilePath);
      }
    },
    getFlyoutEl() {
      if (typeof document === "undefined") return null;
      return document.getElementById("sidebar-profile-menu");
    },
    onTriggerClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.profileDropdownOpen) {
        this.closeProfileDropdown();
        return;
      }
      this.openProfileDropdown();
    },
    onTriggerKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      this.onTriggerClick(event);
    },
    openProfileDropdown() {
      this.presentationMode = "flyout";
      this.menuView = "main";
      this.profileDropdownOpen = true;
      this.emitProfileMenuState(true);
      this.$nextTick(() => {
        this.syncMenuPosition();
        this.$nextTick(() => {
          this.syncMenuPosition();
          setTimeout(() => {
            if (this.profileDropdownOpen) {
              this.attachListeners();
            }
          }, 0);
        });
      });
    },
    closeProfileDropdown() {
      const wasOpen = this.profileDropdownOpen;
      this.profileDropdownOpen = false;
      this.menuView = "main";
      this.presentationMode = "flyout";
      this.menuStyle = {};
      this.detachListeners();
      if (wasOpen) {
        this.emitProfileMenuState(false);
      }
    },
    attachListeners() {
      this.detachListeners();
      window.addEventListener("pointerdown", this.onOutsidePointerDown, true);
      window.addEventListener("keydown", this.onEscapeKey);
      if (this.presentationMode === "flyout") {
        window.addEventListener("resize", this.syncMenuPosition);
        window.addEventListener("scroll", this.syncMenuPosition, true);
      }
    },
    detachListeners() {
      window.removeEventListener("pointerdown", this.onOutsidePointerDown, true);
      window.removeEventListener("keydown", this.onEscapeKey);
      window.removeEventListener("resize", this.syncMenuPosition);
      window.removeEventListener("scroll", this.syncMenuPosition, true);
    },
    onOutsidePointerDown(event) {
      if (!this.profileDropdownOpen) return;
      // Bottom-nav profile tab handles its own toggle on click; ignore its pointerdown.
      if (typeof event.target?.closest === "function" && event.target.closest(".mobile-bottom-nav__tab--button")) {
        return;
      }
      const trigger = this.$refs.trigger;
      const flyout = this.getFlyoutEl();
      if (trigger?.contains(event.target) || flyout?.contains(event.target)) {
        return;
      }
      this.closeProfileDropdown();
    },
    onEscapeKey(event) {
      if (!this.profileDropdownOpen) return;
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (this.menuView === "help") {
        this.menuView = "main";
        return;
      }
      this.closeProfileDropdown();
    },
    syncMenuPosition() {
      if (!this.profileDropdownOpen || this.presentationMode !== "flyout") return;
      const trigger = this.$refs.trigger;
      const flyout = this.getFlyoutEl();
      if (!trigger || !flyout) return;

      const rect = trigger.getBoundingClientRect();
      const flyoutHeight = flyout.offsetHeight || 0;
      const flyoutWidth = flyout.offsetWidth || 240;
      const gap = 8;
      const padding = 12;

      let left = rect.right + gap;
      let top = rect.bottom - flyoutHeight;

      if (left + flyoutWidth > window.innerWidth - padding) {
        left = Math.max(padding, rect.left - flyoutWidth - gap);
      }
      if (top < padding) {
        top = padding;
      }
      if (top + flyoutHeight > window.innerHeight - padding) {
        top = Math.max(padding, window.innerHeight - flyoutHeight - padding);
      }

      this.menuStyle = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 1060,
      };
    },
    isLocaleActive(code) {
      const loc = String(this.$i18n?.locale || "").toLowerCase();
      if (code === "es") return loc === "es" || loc.startsWith("es");
      if (code === "en") return loc === "en" || loc.startsWith("en");
      return false;
    },
    async onSelectLanguage(locale) {
      const target = locale === "en" ? "en" : "es";
      if (this.isSavingLanguage) return;
      if (this.isLocaleActive(target)) {
        this.closeProfileDropdown();
        return;
      }
      const userId = this.currentUserStore.currentUserId;
      if (!userId) {
        this.$notify({
          message: this.tt(
            "userProfile.sessionError",
            "Error: User session not found. Please log in again."
          ),
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return;
      }
      this.isSavingLanguage = true;
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(
            this.tt("userProfile.sessionError", "Error: User session not found. Please log in again.")
          );
        }
        const apiUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
        const response = await fetch(`${apiUrl}/momo/get-users/user-default-lenguage`, {
          method: "PATCH",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            user_id: userId,
            user_default_lenguage: target,
          }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          throw new Error(
            errData?.detail ||
              response.statusText ||
              this.tt("userProfile.languageSaveError", "Error saving language")
          );
        }
        await this.currentUserStore.refresh(session);
        initializeLocale(this.currentUserStore.savedLocale);
        this.$notify({
          message: this.tt("userProfile.languageSaved", "Language updated successfully"),
          icon: "ti-check",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "success",
        });
        this.closeProfileDropdown();
      } catch (error) {
        console.error("Error saving language from sidebar:", error);
        this.$notify({
          message: `${this.tt("userProfile.languageSaveError", "Error saving language")}: ${
            error.message || error
          }`,
          icon: "ti-alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } finally {
        this.isSavingLanguage = false;
      }
    },
    async handleLogout() {
      try {
        // 1. Attempt global sign out (server-side)
        const { error } = await supabase.auth.signOut();

        if (error) {
          console.warn("Server-side logout failed (non-fatal):", error.message);
        }
      } catch (error) {
        console.warn("Logout exception (non-fatal):", error);
      } finally {
        // 2. Force local sign out to clear SDK state regardless of server response
        // This fixes issues where a 403 (session not found) prevents local cleanup
        await supabase.auth.signOut({ scope: "local" });

        // 3. Manual storage cleanup as a failsafe
        try {
          const supabaseUrl = process.env.VUE_APP_SUPABASE_URL;
          if (supabaseUrl) {
            // Default Supabase storage key format: sb-<project-ref>-auth-token
            const projectRef = new URL(supabaseUrl).hostname.split(".")[0];
            const storageKey = `sb-${projectRef}-auth-token`;

            localStorage.removeItem(storageKey);
            sessionStorage.removeItem(storageKey);
          }
        } catch (e) {
          console.warn("Manual storage cleanup failed:", e);
        }

        // 4. Clear custom app state
        localStorage.removeItem("oauth_origin");

        // 5. Redirect to website home.
        leaveAppForWebsite({ locale: this.$i18n.locale });
      }
    },
  },
};
</script>

<style scoped>
.sidebar-profile {
  position: relative;
  overflow: visible;
  padding: 0;
}

.sidebar-profile-pill__chevron {
  display: none;
}

.sidebar-profile-pill__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  background: rgba(0, 0, 0, 0.55);
}

.sidebar-profile-pill__menu {
  width: 240px;
  min-width: 240px;
  margin: 0;
  padding: 6px;
  list-style: none;
  border-radius: var(--clipr-radius-md);
  background: var(--clipr-bg-elevated);
  border: 1px solid var(--clipr-border-color);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(168, 85, 247, 0.12);
  overflow: visible;
}

.sidebar-profile-pill__menu--modal {
  width: min(280px, calc(100vw - 40px));
  min-width: min(280px, calc(100vw - 40px));
  max-width: min(280px, calc(100vw - 40px));
}

@media (max-width: 991px) {
  .sidebar-profile-pill__menu--modal {
    width: calc(100vw - 16px);
    min-width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
    padding: 12px 10px;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-language,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-profile,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-logout,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-help,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-back,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-action {
    border-top: none;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-profile {
    padding-top: 0;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-help {
    margin-top: 0;
    padding: 4px 6px 10px;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__tokens-panel {
    padding: 14px 12px 12px;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__tokens-label,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__language-label {
    font-size: 0.78rem;
    letter-spacing: 0.05em;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__tokens-icon {
    font-size: 1.25rem;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__tokens-amount {
    font-size: 1.75rem;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__tokens-hint {
    margin-top: 8px;
    font-size: 0.85rem;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__tokens-renewal {
    margin-top: 6px;
    font-size: 0.8rem;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__profile-btn,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__help-btn,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__back-btn,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__action-btn,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__logout-btn,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__language-btn {
    min-height: 48px;
    padding: 12px 14px;
    gap: 10px;
    font-size: 0.95rem;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__profile-btn i,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__help-btn i,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__back-btn i,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__action-btn i,
  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__logout-btn i {
    font-size: 1.05rem;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__language-label {
    padding: 0 12px 10px;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__language-row {
    gap: 10px;
    padding: 0 6px 6px;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__coming-soon {
    top: -9px;
    padding: 2px 8px;
    font-size: 0.65rem;
  }

  .sidebar-profile-pill__menu--modal .sidebar-profile-pill__menu-action--soon {
    padding-top: 14px;
  }
}

.sidebar-profile-pill__tokens-panel {
  padding: 12px 12px 10px;
  text-align: center;
}

.sidebar-profile-pill__tokens-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--clipr-text-secondary);
}

.sidebar-profile-pill__tokens-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  font-variant-numeric: tabular-nums;
  color: #e8c547;
}

.sidebar-profile-pill__tokens-value--low {
  color: #f87171;
}

.sidebar-profile-pill__tokens-value--low .sidebar-profile-pill__tokens-icon {
  color: #f87171;
}

.sidebar-profile-pill__tokens-icon {
  font-size: 1rem;
  color: #e8c547;
}

.sidebar-profile-pill__tokens-amount {
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1;
}

.sidebar-profile-pill__tokens-hint {
  margin-top: 6px;
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1.3;
  color: var(--clipr-text-secondary);
}

.sidebar-profile-pill__tokens-renewal {
  margin-top: 4px;
  font-size: 0.64rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--clipr-purple-light);
}

.sidebar-profile-pill__menu-language {
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid var(--clipr-border-color);
}

.sidebar-profile-pill__menu-profile {
  margin-top: 6px;
  padding: 8px 6px 2px;
  border-top: 1px solid var(--clipr-border-color);
}

.sidebar-profile-pill__profile-btn,
.sidebar-profile-pill__help-btn,
.sidebar-profile-pill__back-btn,
.sidebar-profile-pill__action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-sm);
  background: rgba(0, 0, 0, 0.2);
  color: var(--clipr-text-primary);
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  box-sizing: border-box;
}

.sidebar-profile-pill__profile-btn:hover,
.sidebar-profile-pill__profile-btn:focus-visible,
.sidebar-profile-pill__help-btn:hover,
.sidebar-profile-pill__help-btn:focus-visible,
.sidebar-profile-pill__back-btn:hover,
.sidebar-profile-pill__back-btn:focus-visible,
.sidebar-profile-pill__action-btn:hover:not(:disabled),
.sidebar-profile-pill__action-btn:focus-visible:not(:disabled) {
  background: var(--clipr-purple-overlay-medium);
  border-color: var(--clipr-purple-light);
  outline: none;
  color: var(--clipr-text-primary);
  text-decoration: none;
}

.sidebar-profile-pill__language-label {
  padding: 0 12px 8px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--clipr-text-secondary);
}

.sidebar-profile-pill__language-row {
  display: flex;
  gap: 8px;
  padding: 0 6px 4px;
}

.sidebar-profile-pill__language-btn {
  flex: 1 1 0;
  min-width: 96px;
  padding: 8px 10px;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-sm);
  background: rgba(0, 0, 0, 0.2);
  color: var(--clipr-text-primary);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.sidebar-profile-pill__language-btn:hover:not(:disabled),
.sidebar-profile-pill__language-btn:focus-visible {
  background: var(--clipr-purple-overlay-medium);
  outline: none;
}

.sidebar-profile-pill__language-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.sidebar-profile-pill__language-btn--active {
  border-color: var(--clipr-purple-light);
  background: var(--clipr-purple-overlay-medium);
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.35);
}

.sidebar-profile-pill__menu-logout,
.sidebar-profile-pill__menu-help,
.sidebar-profile-pill__menu-back,
.sidebar-profile-pill__menu-action {
  margin-top: 6px;
  padding: 8px 6px 2px;
  border-top: 1px solid var(--clipr-border-color);
}

.sidebar-profile-pill__menu-action + .sidebar-profile-pill__menu-action {
  margin-top: 0;
  padding-top: 4px;
  border-top: none;
}

.sidebar-profile-pill__menu-action.sidebar-profile-pill__menu-action--soon {
  padding-top: 20px;
}

.sidebar-profile-pill__logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-sm);
  background: rgba(0, 0, 0, 0.2);
  color: var(--clipr-text-primary);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.sidebar-profile-pill__logout-btn:hover,
.sidebar-profile-pill__logout-btn:focus-visible {
  background: rgba(248, 113, 113, 0.12);
  border-color: rgba(248, 113, 113, 0.4);
  color: #fca5a5;
  outline: none;
}

.sidebar-profile-pill__action-btn--whatsapp {
  border-color: rgba(37, 211, 102, 0.5);
  background: rgba(37, 211, 102, 0.12);
  color: var(--clipr-text-primary);
}

.sidebar-profile-pill__action-btn--whatsapp i {
  color: #25d366;
}

.sidebar-profile-pill__action-btn--whatsapp:hover,
.sidebar-profile-pill__action-btn--whatsapp:focus-visible {
  border-color: rgba(37, 211, 102, 0.75);
  background: rgba(37, 211, 102, 0.2);
  color: var(--clipr-text-primary);
}

.sidebar-profile-pill__action-btn--disabled {
  position: relative;
  opacity: 0.7;
  cursor: not-allowed;
}

.sidebar-profile-pill__coming-soon {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.3;
  white-space: nowrap;
  color: var(--clipr-citrus-light);
  border: 1px solid var(--clipr-citrus-border);
  background: rgba(15, 15, 24, 0.95);
  pointer-events: none;
}
</style>
